import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PackageSearch, ShoppingBag, Store, Bike } from "lucide-react";

const roleHome = (role) => ({ merchant: "/merchant", courier: "/courier", admin: "/admin", customer: "/app" }[role] || "/app");

const ROLES = [
  { key: "customer", label: "Shop", icon: ShoppingBag, sub: "Buy & get delivery" },
  { key: "merchant", label: "Sell", icon: Store, sub: "Run a store" },
  { key: "courier", label: "Deliver", icon: Bike, sub: "Earn as a courier" },
];

export default function Register() {
  const [params] = useSearchParams();
  const initialRole = ROLES.some((r) => r.key === params.get("role")) ? params.get("role") : "customer";
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: initialRole });
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      toast.success("Account created");
      navigate(roleHome(u.role), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  const googleSignup = () => {
    localStorage.setItem("drivons_signup_role", form.role);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-slate-950 text-white relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-brand/20 rounded-full blur-[120px]" />
        <Link to="/" className="relative flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center"><PackageSearch className="w-5 h-5 text-white" /></div>
          <span className="font-display font-extrabold text-lg">Drivons<span className="text-brand">Market</span></span>
        </Link>
        <div className="relative">
          <h2 className="text-4xl font-display font-black leading-tight">Join the marketplace<br />built for <span className="text-brand">real goods.</span></h2>
          <p className="text-slate-400 mt-4 max-w-sm">Shop, sell or deliver — one platform for local non-food retail.</p>
        </div>
        <div className="relative text-xs text-slate-500 font-mono">ISRAEL · TURKEY · UAE</div>
      </div>

      <div className="flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="flex justify-end mb-2"><LanguageSwitcher /></div>
          <h1 className="text-2xl font-display font-bold">{t("auth.createTitle")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("auth.createSub")}</p>

          <div className="grid grid-cols-3 gap-2 mt-5">
            {ROLES.map((r) => (
              <button
                key={r.key}
                type="button"
                data-testid={`role-${r.key}`}
                onClick={() => setForm({ ...form, role: r.key })}
                className={`rounded-xl border p-3 text-center transition-all ${form.role === r.key ? "border-brand bg-accent" : "hover:border-brand/50"}`}
              >
                <r.icon className={`w-5 h-5 mx-auto mb-1.5 ${form.role === r.key ? "text-brand" : "text-muted-foreground"}`} />
                <div className="text-xs font-semibold">{r.label}</div>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-3 mt-5">
            <div><Label>Full name</Label><Input data-testid="reg-name" value={form.name} onChange={set("name")} required className="mt-1 h-11 rounded-xl" /></div>
            <div><Label>Email</Label><Input data-testid="reg-email" type="email" value={form.email} onChange={set("email")} required className="mt-1 h-11 rounded-xl" /></div>
            <div><Label>Phone</Label><Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} className="mt-1 h-11 rounded-xl" placeholder="Optional" /></div>
            <div><Label>Password</Label><Input data-testid="reg-password" type="password" value={form.password} onChange={set("password")} required className="mt-1 h-11 rounded-xl" /></div>
            <Button data-testid="reg-submit" disabled={busy} className="w-full h-11 rounded-xl bg-brand hover:bg-brand-dark">{busy ? "Creating…" : "Create account"}</Button>
          </form>

          <div className="flex items-center gap-3 my-4"><div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">or</span><div className="h-px bg-border flex-1" /></div>
          <Button data-testid="google-signup-btn" type="button" onClick={googleSignup} variant="outline" className="w-full h-11 rounded-xl gap-2">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4" />
            {t("auth.continueGoogle")}
          </Button>

          <p className="text-sm text-center mt-5 text-muted-foreground">Have an account? <Link to="/login" className="text-brand font-semibold">Log in</Link></p>
        </div>
      </div>
    </div>
  );
}
